/* eslint-disable max-lines */
/**
 * @file  Stock Chart
 * @author yuanyaojia@baijia.com
 * @date 2021-04-21
 */

import React, {useEffect, useContext, useState} from 'react';
import {Button, message} from 'antd';
import ReactEcharts from 'echarts-for-react';
import * as echarts from 'echarts';
import moment from 'moment';
import {getStockInfo, getMinKLine, getPeriodKLine} from '~/service/stock';
import {BasicLayoutContext} from '~/layout/BasicLayout';

import './index.less';

const MIN = 1;
const DAY = 2;
const WEEK = 3;
const MONTH = 4;
const YEAR = 5;
const QUARTER = 11;

const upColor = '#ec0000';
const upBorderColor = '#8A0000';
const downColor = '#00da3c';
const downBorderColor = '#008F28';

// eslint-disable-next-line max-lines-per-function
function Chart() {
    const [exchange, setExchange] = useState('');
    const [code, setCode] = useState('');
    const [currentPrice, setCurrentPrice] = useState('');
    const [openPrice, setOpenPrice] = useState('');
    const [priceUpAndDown, setPriceUpAndDown] = useState('');
    const [priceUpAndDownPercent, setPriceUpAndDownPercent] = useState(0);
    const [lowPrice, setLowPrice] = useState('');
    const [highPrice, setHighPrice] = useState('');
    const [lowFiftyTwoWeek, setLowFiftyTwoWeek] = useState('');
    const [highFiftyTwoWeek, setHighFiftyTwoWeek] = useState('');
    const [volume, setVolume] = useState('');
    const [marketCapital, setMarketCapital] = useState('');
    const [stockPrice, setStockPrice] = useState([]);
    const [priceDs, setPriceDs] = useState([]);
    const [isLine, setIsLine] = useState(true);
    const [minActive, setMinActive] = useState(false);
    const [dayActive, setDayActive] = useState(false);
    const [weekActive, setWeekActive] = useState(false);
    const [monthActive, setMonthActive] = useState(false);
    const [quarterActive, setQuarterActive] = useState(false);
    const [yearActive, setYearActive] = useState(false);
    const layoutContext = useContext(BasicLayoutContext);
    const {isMobile} = layoutContext ?? {};

    // eslint-disable-next-line max-lines-per-function
    function getLineOption() {
        return {
            dataset: {
                source: priceDs
            },
            grid: [
                {
                    bottom: 100
                }
            ],
            dataZoom: [{type: 'inside'}, {}],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                position(pos, params, el, elRect, size) {
                    const obj = {top: 10};
                    obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
                    return obj;
                },
                extraCssText: 'width: 170px'
            },
            axisPointer: {
                link: {xAxisIndex: 'all'},
                label: {
                    backgroundColor: '#777'
                }
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                axisLine: {onZero: false}
            },
            yAxis: [
                {
                    type: 'value',
                    boundaryGap: [0, '100%'],
                    scale: true,
                    splitNumber: 4
                },
                {
                    type: 'value',
                    scale: true,
                    splitNumber: 4
                }
            ],
            series: [
                {
                    name: 'Price',
                    symbol: 'none',
                    sampling: 'lttb',
                    itemStyle: {
                        color: 'rgb(255, 70, 131)'
                    },
                    axisPointer: {
                        z: 100
                    },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: 'rgb(255, 158, 68)'
                            }, {
                                offset: 1,
                                color: 'rgb(255, 70, 131)'
                            }
                        ])
                    },
                    type: 'line',
                    yAxisIndex: 0,
                    lineStyle: {
                        width: 1
                    }
                },
                {
                    name: 'Radio',
                    type: 'line',
                    yAxisIndex: 1,
                    symbol: 'none',
                    lineStyle: {
                        width: 1,
                        color: 'transparent',
                    }
                }
            ]
        };
    }

    function fetchMinKLine(type) {
        getMinKLine({
            type
        })
            .then(({data}) => {
                const {priceArray} = data;
                setPriceDs(
                    priceArray.map(
                        ({
                            time,
                            price,
                            ratio
                        }) => [(moment.unix(time).format('YYYY/MM/DD HH:mm:ss')), price, ratio]
                    )
                );
            })
            .catch(info => {
                message.error('获取股票曲线失败');
            });
    }

    function handleMinutes() {
        setMinActive(true);
        setDayActive(false);
        setWeekActive(false);
        setMonthActive(false);
        setQuarterActive(false);
        setYearActive(false);
        setIsLine(true);
        // fetchMinKLine(MIN);
    }

    useEffect(() => {
        getStockInfo({})
            .then(({data}) => {
                const {
                    exchange,
                    code,
                    currentPrice,
                    openPrice,
                    priceUpAndDown,
                    priceUpAndDownPercent,
                    lowPrice,
                    highPrice,
                    lowFiftyTwoWeek,
                    highFiftyTwoWeek,
                    volume,
                    marketCapital
                } = data;
                setExchange(exchange);
                setCode(code);
                setCurrentPrice(currentPrice);
                setOpenPrice(openPrice);
                setPriceUpAndDown(priceUpAndDown);
                setPriceUpAndDownPercent(priceUpAndDownPercent);
                setLowPrice(lowPrice);
                setHighPrice(highPrice);
                setLowFiftyTwoWeek(lowFiftyTwoWeek);
                setHighFiftyTwoWeek(highFiftyTwoWeek);
                setVolume(volume);
                setMarketCapital(marketCapital);
            })
            .catch(info => {
                message.error('获取股票基本信息失败');
            });
        handleMinutes();
    }, []);

    // eslint-disable-next-line max-lines-per-function
    function getOption() {
        return {
            dataset: {
                source: stockPrice
            },
            tooltip: {
                trigger: 'axis',
                position(pt) {
                    return [pt[0], '10%'];
                }
            },
            grid: [
                {
                    left: '10%',
                    right: '10%',
                    bottom: 150
                },
                {
                    left: '10%',
                    right: '10%',
                    height: 50,
                    bottom: 80
                }
            ],
            xAxis: [
                {
                    type: 'category',
                    scale: true,
                    boundaryGap: false,
                    // inverse: true,
                    axisLine: {onZero: false},
                    splitLine: {show: false},
                    splitNumber: 20,
                    min: 'dataMin',
                    max: 'dataMax'
                },
                {
                    type: 'category',
                    gridIndex: 1,
                    scale: true,
                    boundaryGap: false,
                    axisLine: {onZero: false},
                    axisTick: {show: false},
                    splitLine: {show: false},
                    axisLabel: {show: false},
                    splitNumber: 20,
                    min: 'dataMin',
                    max: 'dataMax'
                }
            ],
            yAxis: [
                {
                    scale: true,
                    splitArea: {
                        show: true
                    }
                },
                {
                    scale: true,
                    gridIndex: 1,
                    splitNumber: 2,
                    axisLabel: {show: false},
                    axisLine: {show: false},
                    axisTick: {show: false},
                    splitLine: {show: false}
                }
            ],
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: [0, 1],
                    start: 10,
                    end: 100
                },
                {
                    show: true,
                    xAxisIndex: [0, 1],
                    type: 'slider',
                    bottom: 10,
                    start: 10,
                    end: 100
                }
            ],
            visualMap: {
                show: false,
                seriesIndex: 1,
                dimension: 6,
                pieces: [
                    {
                        value: 1,
                        color: upColor
                    }, {
                        value: -1,
                        color: downColor
                    }
                ]
            },
            series: [
                {
                    type: 'candlestick',
                    itemStyle: {
                        color: upColor,
                        color0: downColor,
                        borderColor: upBorderColor,
                        borderColor0: downBorderColor
                    },
                    encode: {
                        x: 0,
                        y: [1, 4, 3, 2]
                    }
                },
                {
                    name: 'Volumn',
                    type: 'bar',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    itemStyle: {
                        color: '#7fbe9e'
                    },
                    large: true,
                    encode: {
                        x: 0,
                        y: 5
                    }
                }
            ]
        };
    }

    function fetchPeriodKLine(type) {
        getPeriodKLine({
            type
        })
            .then(({data: {list}}) => {
                setStockPrice(
                    list.map(
                        ({
                            time,
                            openPrice,
                            highestPrice,
                            lowestPrice,
                            closePrice,
                            volume
                        }) => [
                            (moment.unix(time).format('YYYY/MM/DD')),
                            openPrice,
                            highestPrice,
                            lowestPrice,
                            closePrice,
                            volume,
                            -1
                        ]
                    )
                );
            })
            .catch(info => {
                message.error('获取股票曲线失败');
            });
    }

    function handleFiveDays() {
        setMinActive(false);
        setDayActive(true);
        setWeekActive(false);
        setMonthActive(false);
        setQuarterActive(false);
        setYearActive(false);
        setIsLine(true);
        fetchMinKLine(DAY);
    }

    function handleOneWeek() {
        setMinActive(false);
        setDayActive(false);
        setWeekActive(true);
        setMonthActive(false);
        setQuarterActive(false);
        setYearActive(false);
        setIsLine(false);
        fetchPeriodKLine(WEEK);
    }

    function handleOneMonth() {
        setMinActive(false);
        setDayActive(false);
        setWeekActive(false);
        setMonthActive(true);
        setQuarterActive(false);
        setYearActive(false);
        setIsLine(false);
        fetchPeriodKLine(MONTH);
    }

    function handleThreeMouth() {
        setMinActive(false);
        setDayActive(false);
        setWeekActive(false);
        setMonthActive(false);
        setQuarterActive(true);
        setYearActive(false);
        setIsLine(false);
        fetchPeriodKLine(QUARTER);
    }

    function handleOneYear() {
        setMinActive(false);
        setDayActive(false);
        setWeekActive(false);
        setMonthActive(false);
        setQuarterActive(false);
        setYearActive(true);
        setIsLine(false);
        fetchPeriodKLine(YEAR);
    }

    return (
        <div className="chart-container">
            {
                isMobile && (
                    <div className="chart-container-title">
                        Stock Quote & Chart
                    </div>
                )
            }
            <div className="chart-stock">
                <div className="stock-news-info">
                    <div className="stock-info">
                        <span className="stock-code">
                            {exchange}
                            {' '}
                            :
                            {' '}
                            {code}
                        </span>
                        <span className="stock-price">
                            $
                            {' '}
                            {currentPrice}
                        </span>
                        <span className="stock-prompt">
                            Currency in USD.
                        </span>
                        <span className="stock-time">
                            Quote data delayed by at least 15 minutes
                        </span>
                    </div>
                </div>

                <div className="news-kanban">
                    <div className="news-info">
                        <div className="news">
                            <div className="title">Open</div>
                            <div className="title-info">
                                $
                                {' '}
                                {openPrice}
                            </div>
                        </div>

                        <div className="news">
                            <div className="title">Change</div>
                            <div className="title-info">
                                $
                                {' '}
                                {priceUpAndDown}
                                {' '}
                                (
                                {(priceUpAndDownPercent < 0 ? '' : '+') + priceUpAndDownPercent}
                                %)
                            </div>
                        </div>
                    </div>

                    <div className="news-info-two">
                        <div className="news">
                            <div className="title">
                                Day‘s Range
                            </div>
                            <div className="title-info">
                                $
                                {' '}
                                {lowPrice}
                                {' '}
                                - $
                                {' '}
                                {highPrice}
                            </div>
                        </div>

                        <div className="news">
                            <div className="title">52-Week Range</div>
                            <div className="title-info">
                                $
                                {' '}
                                {lowFiftyTwoWeek}
                                {' '}
                                - $
                                {' '}
                                {highFiftyTwoWeek}
                            </div>
                        </div>

                    </div>

                    <div className="news-info-three">
                        <div className="news">
                            <div className="title">Volume</div>
                            <div className="title-info">
                                {volume}
                            </div>
                        </div>

                        <div className="news">
                            <div className="title">Market Cap</div>
                            <div className="title-info">
                                $
                                {' '}
                                {marketCapital}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="k-line-btn">
                <Button
                    type="text"
                    onClick={handleMinutes}
                    className={minActive ? 'minute-active' : ''}
                >
                    minutes
                </Button>
                <Button
                    type="text"
                    onClick={handleFiveDays}
                    className={dayActive ? 'day-active' : ''}
                >
                    5days
                </Button>
                <Button
                    type="text"
                    onClick={handleOneWeek}
                    className={weekActive ? 'week-active' : ''}
                >
                    1week
                </Button>
                <Button
                    type="text"
                    onClick={handleOneMonth}
                    className={monthActive ? 'month-active' : ''}
                >
                    1month
                </Button>
                <Button
                    type="text"
                    onClick={handleThreeMouth}
                    className={quarterActive ? 'quarter-active' : ''}
                >
                    3months
                </Button>
                <Button
                    type="text"
                    onClick={handleOneYear}
                    className={yearActive ? 'year-active' : ''}
                >
                    1year
                </Button>
            </div>

            <div className="stock-k-line">
                <ReactEcharts
                    option={isLine ? getLineOption() : getOption()}
                    lazyUpdate
                    notMerge
                />
            </div> */}
        </div>
    );
}

export default Chart;
